class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Filter & Sort drawer: keep Remove All / Apply in sync with the real selection (runs once per form element).
    if (facetForm.id === 'FacetFiltersFormMobile') {
      const syncActionButtons = () => FacetFiltersForm.updateActionButtons();
      facetForm.addEventListener('input', syncActionButtons);
      facetForm.addEventListener('change', syncActionButtons);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  // True when at least one filter (checkbox/radio value or price range) is selected in the drawer form.
  // Sorting (sort_by) is not a filter. Reads live DOM state, so URL-applied filters and AJAX re-renders are covered.
  static hasSelectedFilters(form) {
    const hasChoice = Array.from(form.elements).some(
      (el) => el.checked && (el.type === 'checkbox' || el.type === 'radio') && el.name !== 'sort_by'
    );
    if (hasChoice) return true;

    const toNumber = (value) => parseFloat(String(value == null ? '' : value).replace(/[^\d.]/g, ''));
    return Array.from(form.querySelectorAll('.main-range-container')).some((container) => {
      const textInputs = container.querySelectorAll('.range-input-text');
      const rangeInputs = container.querySelectorAll('.range-input');
      if (textInputs.length < 2 || rangeInputs.length < 2) return false;
      const min = toNumber(textInputs[0].value);
      const max = toNumber(textInputs[1].value);
      const floor = toNumber(rangeInputs[0].min) || 0;
      const ceiling = toNumber(rangeInputs[1].max);
      return (!isNaN(min) && min > floor) || (!isNaN(max) && !isNaN(ceiling) && max < ceiling);
    });
  }

  static setActionState(element, disable) {
    element.toggleAttribute('disabled', disable);
    element.setAttribute('aria-disabled', String(disable));
    if (disable) element.setAttribute('tabindex', '-1');
    else element.removeAttribute('tabindex');
    const wrapper = element.closest('facet-remove');
    if (wrapper) wrapper.classList.toggle('is-disabled', disable);
  }

  // Enables / disables the drawer's Remove All button and each sub-filter's Clear button.
  static updateActionButtons() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;

    const actions = form.querySelector('[data-facets-actions]');
    if (actions) {
      const disable = !FacetFiltersForm.hasSelectedFilters(form);
      actions.querySelectorAll('[data-facets-action]').forEach((el) => FacetFiltersForm.setActionState(el, disable));
    }

    // Clear: enabled only when that filter group has a selection.
    form.querySelectorAll('[data-facets-clear]').forEach((el) => {
      const group = el.closest('.js-filter');
      const hasSelection = !!group && !!group.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
      FacetFiltersForm.setActionState(el, !hasSelection);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        window.initPriceSlider();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    window.initPriceSlider();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    if (this.querySelector('a').hasAttribute('disabled')) return;
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


window.initPriceSlider = function () {
  document.querySelectorAll('.main-range-container').forEach(function (container) {
    initOnePriceSlider(container);
  });
  // Runs on page load and after every AJAX filter render: refresh Remove All / Apply state.
  FacetFiltersForm.updateActionButtons();
};

function initOnePriceSlider(container) {
  var rangeMin = container.querySelector('#range-min-value');
  var rangeMax = container.querySelector('#range-max-value');
  var inputMin = container.querySelector('#input-min-value');
  var inputMax = container.querySelector('#input-max-value');
  var progress = container.querySelector('.progress');
  if (!rangeMin || !rangeMax || !inputMin || !inputMax || !progress) return;
  if (container._psCleanup) container._psCleanup();
  function cleanNum(v){
    v = String(v == null ? '' : v).replace(/[^\d.]/g, '');
    v = parseFloat(v);
    return isNaN(v) ? NaN : Math.round(v);
  }
  var MAX = cleanNum(inputMax.getAttribute('data-max-price'));
  if (isNaN(MAX) || MAX <= 0) MAX = 100;
  var MIN = 0;
  var GAP = 1;
  rangeMin.min = MIN; rangeMin.max = MAX; rangeMin.step = 1;
  rangeMax.min = MIN; rangeMax.max = MAX; rangeMax.step = 1;
  function lo(){ return parseInt(rangeMin.value, 10); }
  function hi(){ return parseInt(rangeMax.value, 10); }
  function paint(){
    var l = lo(), h = hi();
    progress.style.left  = ((l - MIN) / (MAX - MIN) * 100) + '%';
    progress.style.right = (100 - (h - MIN) / (MAX - MIN) * 100) + '%';
    rangeMin.style.zIndex = (h - l < (MAX - MIN) * 0.1) ? 5 : 3;
  }

  function onMinInput(){
    var l = lo(), h = hi();
    if (l > h - GAP){ l = h - GAP; rangeMin.value = l; }
    inputMin.value = l; paint();
  }
  function onMaxInput(){
    var l = lo(), h = hi();
    if (h < l + GAP){ h = l + GAP; rangeMax.value = h; }
    inputMax.value = h; paint();
  }
  function commitMin(){
    var v = cleanNum(inputMin.value);
    if (isNaN(v)) v = MIN;
    v = Math.max(MIN, Math.min(v, hi() - GAP));
    rangeMin.value = v; inputMin.value = v; paint();
  }
  function commitMax(){
    var v = cleanNum(inputMax.value);
    if (isNaN(v)) v = MAX;
    v = Math.min(MAX, Math.max(v, lo() + GAP));
    rangeMax.value = v; inputMax.value = v; paint();
  }
  function onMinKey(e){ if (e.key === 'Enter'){ e.preventDefault(); commitMin(); } }
  function onMaxKey(e){ if (e.key === 'Enter'){ e.preventDefault(); commitMax(); } }

  rangeMin.addEventListener('input', onMinInput);
  rangeMax.addEventListener('input', onMaxInput);
  inputMin.addEventListener('change', commitMin);
  inputMax.addEventListener('change', commitMax);
  inputMin.addEventListener('keydown', onMinKey);
  inputMax.addEventListener('keydown', onMaxKey);

  container._psCleanup = function(){
    rangeMin.removeEventListener('input', onMinInput);
    rangeMax.removeEventListener('input', onMaxInput);
    inputMin.removeEventListener('change', commitMin);
    inputMax.removeEventListener('change', commitMax);
    inputMin.removeEventListener('keydown', onMinKey);
    inputMax.removeEventListener('keydown', onMaxKey);
  };

  var startMin = cleanNum(inputMin.value);
  var startMax = cleanNum(inputMax.value);
  rangeMin.value = !isNaN(startMin) ? Math.max(MIN, Math.min(startMin, MAX)) : MIN;
  rangeMax.value = !isNaN(startMax) ? Math.max(MIN, Math.min(startMax, MAX)) : MAX;
  inputMin.value = lo();
  inputMax.value = hi();
  paint();
}

if (document.readyState !== 'loading') {
  window.initPriceSlider();
} else {
  document.addEventListener('DOMContentLoaded', window.initPriceSlider);
}